import React, { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { format, parseISO } from 'date-fns';
import { Plus, Check, X } from 'lucide-react';
import { Card } from '../../components/ui/Card';
import { Button } from '../../components/ui/Button';
import { Badge } from '../../components/ui/Badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../components/ui/Table';
import toast from 'react-hot-toast';
import { allowanceService } from '../../services/allowance.service';
import { Allowance, AllowanceStatus } from '../../types';
import { CalculateAllowanceModal } from './CalculateAllowanceModal';
import { RejectAllowanceModal } from './RejectAllowanceModal';

export const AllowancesPage: React.FC = () => {
  const queryClient = useQueryClient();
  const [status, setStatus] = useState<AllowanceStatus>(AllowanceStatus.PENDING);
  const [isCalculateOpen, setIsCalculateOpen] = useState(false);
  const [rejectingId, setRejectingId] = useState<number | null>(null);

  const { data, isLoading, error } = useQuery({
    queryKey: ['allowances', status],
    queryFn: () => allowanceService.getByStatus(status),
  });

  const allowances = useMemo<Allowance[]>(() => data || [], [data]);

  const approveMutation = useMutation({
    mutationFn: (id: number) => allowanceService.approve(id),
    onSuccess: async () => {
      toast.success('Allowance approved');
      await queryClient.invalidateQueries({ queryKey: ['allowances'] });
    },
    onError: (err: any) => {
      toast.error(err?.response?.data?.message || 'Failed to approve allowance');
    },
  });

  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat(undefined, { maximumFractionDigits: 0 }).format(value);
  };

  const statusBadgeVariant: Record<AllowanceStatus, 'warning' | 'success' | 'error'> = {
    [AllowanceStatus.PENDING]: 'warning',
    [AllowanceStatus.APPROVED]: 'success',
    [AllowanceStatus.REJECTED]: 'error',
  };

  return (
    <div className="space-y-6">
      <Card
        title="Allowances"
        action={
          <div className="flex items-center gap-2">
            <Button
              variant={status === AllowanceStatus.PENDING ? 'secondary' : 'ghost'}
              size="sm"
              onClick={() => setStatus(AllowanceStatus.PENDING)}
            >
              Pending
            </Button>
            <Button
              variant={status === AllowanceStatus.APPROVED ? 'secondary' : 'ghost'}
              size="sm"
              onClick={() => setStatus(AllowanceStatus.APPROVED)}
            >
              Approved
            </Button>
            <Button
              variant={status === AllowanceStatus.REJECTED ? 'secondary' : 'ghost'}
              size="sm"
              onClick={() => setStatus(AllowanceStatus.REJECTED)}
            >
              Rejected
            </Button>
            <Button size="sm" onClick={() => setIsCalculateOpen(true)}>
              <Plus size={16} className="mr-2" />
              Create
            </Button>
          </div>
        }
      >
        {isLoading && <div className="text-sm text-gray-600">Loading...</div>}
        {!isLoading && error && (
          <div className="text-sm text-error">Failed to load allowances.</div>
        )}

        {!isLoading && !error && (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Trip</TableHead>
                <TableHead>Driver</TableHead>
                <TableHead>Type</TableHead>
                <TableHead>Amount</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Created</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {allowances.length === 0 ? (
                <TableRow>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500" colSpan={7}>
                    No allowances found.
                  </td>
                </TableRow>
              ) : (
                allowances.map((a) => (
                  <TableRow key={a.id}>
                    <TableCell className="font-medium">{a.tripNumber}</TableCell>
                    <TableCell>{a.driverName}</TableCell>
                    <TableCell>{String(a.type)}</TableCell>
                    <TableCell>{formatCurrency(a.amount)}</TableCell>
                    <TableCell>
                      <Badge variant={statusBadgeVariant[a.status]}>{a.status}</Badge>
                    </TableCell>
                    <TableCell>
                      {a.createdAt ? format(parseISO(a.createdAt), 'MMM dd, yyyy') : '-'}
                    </TableCell>
                    <TableCell className="text-right">
                      {a.status === AllowanceStatus.PENDING ? (
                        <div className="flex items-center justify-end gap-2">
                          <Button
                            size="sm"
                            variant="secondary"
                            loading={approveMutation.isPending}
                            onClick={() => approveMutation.mutate(a.id)}
                          >
                            <Check size={16} className="mr-1" />
                            Approve
                          </Button>
                          <Button
                            size="sm"
                            variant="danger"
                            onClick={() => setRejectingId(a.id)}
                          >
                            <X size={16} className="mr-1" />
                            Reject
                          </Button>
                        </div>
                      ) : (
                        <span className="text-sm text-gray-400">-</span>
                      )}
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        )}
      </Card>

      <CalculateAllowanceModal isOpen={isCalculateOpen} onClose={() => setIsCalculateOpen(false)} />
      <RejectAllowanceModal
        isOpen={rejectingId !== null}
        allowanceId={rejectingId}
        onClose={() => setRejectingId(null)}
      />
    </div>
  );
};
